import { SigninRedirectArgs } from 'oidc-client-ts';
import { ReactNode } from 'react';
import { Location, useLocation } from 'react-router-dom';

import {
  ArgsUseAutoSilentRefreshToken,
  OidcRedirectState,
  useAutoLogin,
  useAutoSilentRefreshToken,
  useIsAuthOmitPath,
} from '../hooks';
import {
  ArgsUseHandleSameClientLogout,
  useHandleSameClientLogout,
} from '../hooks/useHandleSameClientLogout';

export type PropsAuthGuard = {
  renderLoadingUI?: () => ReactNode;
  renderComponent?: () => ReactNode;
  extraQueryParams?: SigninRedirectArgs['extraQueryParams'] & {
    sso?:
      | 'microsoft'
      | 'google'
      | 'apple'
      | 'facebook'
      | 'moe'
      | 'knsh'
      | 'clever'
      | 'classlink'
      | 'nyc';
  };
  generateSignInState?: (args: { location: Location }) => OidcRedirectState;
  authOmitPaths?: string[];
} & Pick<
  ArgsUseAutoSilentRefreshToken,
  'onTokenRefreshStart' | 'onTokenRefreshEnd' | 'onTokenRefreshError'
> &
  Pick<ArgsUseHandleSameClientLogout, 'onLogout'>;

export function AuthGuard({
  renderLoadingUI = () => null,
  renderComponent = () => null,
  generateSignInState = () => ({}),
  extraQueryParams,
  authOmitPaths = [],
  onTokenRefreshError,
  onTokenRefreshEnd,
  onTokenRefreshStart,
  onLogout,
}: PropsAuthGuard) {
  const location = useLocation();
  const isOmitAuthPath = useIsAuthOmitPath(authOmitPaths);
  const signInState: OidcRedirectState = {
    refererPath: `${location.pathname}${location.search}${location.hash}`,
    ...generateSignInState({ location }),
  };
  const { done } = useAutoLogin({
    skip: isOmitAuthPath,
    signinRedirectArgs: {
      state: signInState,
      extraQueryParams,
    },
  });

  useHandleSameClientLogout({ onLogout });
  useAutoSilentRefreshToken({
    skip: isOmitAuthPath,
    onTokenRefreshError,
    onTokenRefreshEnd,
    onTokenRefreshStart,
  });

  if (!done) {
    return renderLoadingUI();
  }
  return renderComponent();
}
